import { useState } from 'react'
import { SkillDownloadSurface, SkillOrigin } from '@/analytics/enums/skill.enums'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { trackSkillDownloaded } from '@/analytics/utils/skill-analytics'
import { Button } from '@/components/button/Button'
import { LoadingSkeleton } from '@/components/LoadingSkeleton/LoadingSkeleton'
import { Modal, ModalContent } from '@/components/modal'
import { SearchField } from '@/components/search-field/SearchField'
import { SortableHeaderButton } from '@/components/sortable-header/SortableHeaderButton'
import type { UserSkillsLibrary } from '@/user-skills/hooks/useUserSkills'
import type { PaxtonSkill, UserSkill } from '@/user-skills/schemas'
import { downloadSkillMarkdown } from '@/user-skills/utils/download-skill-markdown'
import UserSkillEditor from './UserSkillEditor'
import DeleteUserSkillDialog from './DeleteUserSkillDialog'
import SkillActionsMenu from './SkillActionsMenu'
import SkillListRow from './SkillListRow'
import SkillViewer from './SkillViewer'

const kLoadingSkeletonRowCount = 4

enum SkillSortColumn {
  NAME = 'name',
  UPDATED = 'updated_at',
}

enum SkillSortDirection {
  ASCENDING = 'asc',
  DESCENDING = 'desc',
}

export default function SkillsDialog({ onClose, library }: { onClose: () => void; library: UserSkillsLibrary }) {
  const { trackEvent } = useAnalytics()
  const { skills, paxtonSkills, loading, loadError, refresh, saveSkill, removeSkill } = library
  const [search, setSearch] = useState('')
  const [sortColumn, setSortColumn] = useState(SkillSortColumn.UPDATED)
  const [sortDirection, setSortDirection] = useState(SkillSortDirection.DESCENDING)
  const sortBy = (column: SkillSortColumn) => {
    if (column === sortColumn) {
      setSortDirection(sortDirection === SkillSortDirection.ASCENDING ? SkillSortDirection.DESCENDING : SkillSortDirection.ASCENDING)
    } else {
      setSortDirection(column === SkillSortColumn.NAME ? SkillSortDirection.ASCENDING : SkillSortDirection.DESCENDING)
    }
    setSortColumn(column)
  }
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingSkill, setEditingSkill] = useState<UserSkill | null>(null)
  const [deletingSkill, setDeletingSkill] = useState<UserSkill | null>(null)
  const [viewingUserSkill, setViewingUserSkill] = useState<UserSkill | null>(null)
  const [viewingPaxtonSkill, setViewingPaxtonSkill] = useState<PaxtonSkill | null>(null)
  const editSkill = (skill: UserSkill | null) => {
    setEditingSkill(skill)
    setEditorOpen(true)
  }
  const query = search.trim().toLowerCase()
  const rows = [
    ...skills.map((skill) => ({
      id: skill.id,
      name: skill.name,
      description: skill.description,
      updatedAt: skill.updated_at,
      author: 'You' as const,
      onOpen: () => setViewingUserSkill(skill),
      actions: (
        <SkillActionsMenu
          name={skill.name}
          onEdit={() => editSkill(skill)}
          onDownload={() => {
            downloadSkillMarkdown(skill.name, skill.description)
            trackSkillDownloaded(trackEvent, SkillOrigin.User, SkillDownloadSurface.Modal, skill.id)
          }}
          onDelete={() => setDeletingSkill(skill)}
        />
      ),
    })),
    ...paxtonSkills.map((skill) => ({
      id: skill.id,
      name: skill.name,
      description: skill.description,
      updatedAt: null,
      author: 'Paxton' as const,
      onOpen: () => setViewingPaxtonSkill(skill),
      actions: (
        <SkillActionsMenu
          name={skill.name}
          onDownload={() => {
            downloadSkillMarkdown(skill.name, skill.description)
            trackSkillDownloaded(trackEvent, SkillOrigin.Paxton, SkillDownloadSurface.Modal, skill.id)
          }}
        />
      ),
    })),
  ]
    .filter((row) => `${row.name} ${row.description}`.toLowerCase().includes(query))
    .sort((a, b) => {
      const comparison =
        sortColumn === SkillSortColumn.NAME
          ? a.name.localeCompare(b.name)
          : (a.updatedAt ? Date.parse(a.updatedAt) : 0) - (b.updatedAt ? Date.parse(b.updatedAt) : 0)
      return (sortDirection === SkillSortDirection.ASCENDING ? comparison : -comparison) || a.name.localeCompare(b.name) || a.id.localeCompare(b.id)
    })
  return (
    <>
      <Modal open={!editorOpen && !deletingSkill && !viewingUserSkill && !viewingPaxtonSkill} onOpenChange={(open) => !open && onClose()}>
        <ModalContent
          title="Skills"
          width="xl"
          height="max-xl"
          showCloseButton
          // Pin top edge so search-driven height changes don't re-center the dialog.
          // Clamp height for the 60px top offset so short viewports don't clip the footer.
          className="top-[60px] max-h-[calc(100dvh-72px)] -translate-y-0"
          bodyClassName="flex min-h-0 flex-col overflow-hidden"
          footer={
            <div className="flex justify-end">
              <Button size="sm" disabled={loading || loadError} onClick={() => editSkill(null)}>
                Create new skill
              </Button>
            </div>
          }
        >
          <div className="shrink-0 pb-4 text-sm text-brand-neutral-950">
            <p>
              Skills let you save instructions once and reuse them, so you don’t have to re-explain the same task every time. Choose which ones are active on
              your account.
            </p>
          </div>
          <div className="shrink-0 pb-4">
            <SearchField aria-label="Search skills" placeholder="Search skills" value={search} onChange={setSearch} />
          </div>
          <div role="region" aria-label="Skills" aria-busy={loading} className="min-h-0 overflow-y-auto rounded border border-brand-neutral-200 bg-white">
            {loading && (
              <span role="status" className="sr-only">
                Loading skills
              </span>
            )}
            <div className="sticky top-0 z-10 grid shrink-0 grid-cols-[minmax(0,1fr)_32px] items-center gap-3 border-b border-brand-neutral-200 bg-brand-neutral-50 px-4 py-2 text-sm font-medium sm:grid-cols-[minmax(0,1fr)_112px_72px_32px] sm:px-6 md:grid-cols-[minmax(0,1fr)_144px_120px_32px]">
              <div>
                <SortableHeaderButton
                  label="Skill name"
                  isSorted={sortColumn === SkillSortColumn.NAME ? sortDirection : false}
                  onSort={() => sortBy(SkillSortColumn.NAME)}
                  ascendingLabel="Sort A–Z"
                  descendingLabel="Sort Z–A"
                />
              </div>
              <div className="hidden sm:block">
                <SortableHeaderButton
                  label="Last updated"
                  isSorted={sortColumn === SkillSortColumn.UPDATED ? sortDirection : false}
                  onSort={() => sortBy(SkillSortColumn.UPDATED)}
                  ascendingLabel="Sort oldest first"
                  descendingLabel="Sort newest first"
                  defaultSortDesc
                />
              </div>
              <span className="hidden truncate text-sm font-medium leading-5 tracking-[-0.15px] text-brand-neutral-950 sm:block">Author</span>
            </div>
            {loading && <SkillsTableSkeletonRows />}
            {loadError && (
              <div role="alert" className="py-6 text-sm">
                Failed to load your skills.{' '}
                <Button variant="plain" size="sm" onClick={() => void refresh()}>
                  Retry
                </Button>
              </div>
            )}
            {!loading && !loadError && rows.map(({ id, description: _description, ...props }) => <SkillListRow key={id} {...props} />)}
            {!loading && !loadError && !rows.length && query && (
              <div role="status" className="flex h-[68px] items-center justify-center">
                <div className="flex max-w-[90%] items-center gap-3 px-2 py-2">
                  <p className="flex min-w-0 items-center text-xs sm:text-sm">
                    <span className="flex-shrink-0">
                      No <span className="hidden sm:inline">Search </span>Results for "
                    </span>
                    <span className="min-w-0 truncate">{search.trim()}</span>
                    <span className="flex-shrink-0">"</span>
                  </p>
                  <Button onClick={() => setSearch('')} size="xs" variant="tonal" color="neutral" aria-label="Clear search">
                    Clear
                  </Button>
                </div>
              </div>
            )}
            {!loading && !loadError && !rows.length && !query && (
              <p className="py-8 text-center text-sm text-brand-neutral-600">Create your first skill using Create new skill.</p>
            )}
          </div>
        </ModalContent>
      </Modal>
      {viewingUserSkill && (
        <SkillViewer
          skill={viewingUserSkill}
          origin={SkillOrigin.User}
          onClose={() => setViewingUserSkill(null)}
          onEdit={() => {
            setViewingUserSkill(null)
            editSkill(viewingUserSkill)
          }}
          onDelete={() => {
            setViewingUserSkill(null)
            setDeletingSkill(viewingUserSkill)
          }}
        />
      )}
      {viewingPaxtonSkill && <SkillViewer skill={viewingPaxtonSkill} origin={SkillOrigin.Paxton} onClose={() => setViewingPaxtonSkill(null)} />}
      {editorOpen && (
        <UserSkillEditor
          skill={editingSkill}
          skillId={editingSkill?.id}
          onClose={() => setEditorOpen(false)}
          onSave={(content) => saveSkill(editingSkill?.id, content)}
        />
      )}
      {deletingSkill && (
        <DeleteUserSkillDialog
          skill={deletingSkill}
          onClose={() => setDeletingSkill(null)}
          onDelete={async () => {
            await removeSkill(deletingSkill.id)
          }}
        />
      )}
    </>
  )
}

function SkillsTableSkeletonRows() {
  return (
    <>
      {Array.from({ length: kLoadingSkeletonRowCount }, (_, index) => (
        // LoadingSkeleton sets its own role="status", but it is inert inside this aria-hidden row —
        // the region's sr-only status owns the announcement.
        <div
          key={index}
          aria-hidden="true"
          className="grid grid-cols-[minmax(0,1fr)_32px] items-center gap-3 border-b border-brand-neutral-200 px-4 py-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_112px_72px_32px] sm:px-6 md:grid-cols-[minmax(0,1fr)_144px_120px_32px]"
        >
          <LoadingSkeleton width="66%" lineHeight={16} />
          <div className="hidden sm:block">
            <LoadingSkeleton width={96} lineHeight={16} />
          </div>
          <div className="hidden sm:block">
            <LoadingSkeleton width={48} lineHeight={16} />
          </div>
          <div />
        </div>
      ))}
    </>
  )
}
